import { Machine } from "./Connections";
// Note: React hooks (useAppInsightsContext) cannot be used inside this class.
// Pass an AppInsights instance from a React component instead.

type TransformFn = (
    settings: any,
    originalSetting: any,
    destinationSetting: any
) => { originalNewSetting: any; destinationNewSetting: any };

export default class SettingsMigration {
    // Stored migration configuration
    originalPath: string;
    destinationPath: string;
    migrationName: string;
    transformFunction?: TransformFn;

    constructor(
        originalPath: string,
        destinationPath: string,
        migrationName: string,
        transformFunction?: TransformFn
    ) {
        this.originalPath = originalPath;
        this.destinationPath = destinationPath;
        this.migrationName = migrationName;
        this.transformFunction = transformFunction;
    }


    
    migrate = async (
        settings: any,
        machine: Machine,
        appInsights: { trackException: (data: any) => void }
    ): Promise<void> => {
        const migrationMarkerRelativePath = `User/Migrations/${this.destinationPath}`; // relative to /Settings

        // Helper to safely traverse nested settings path
        const getSettingFromSegments = (root: any, segments: string[]) => {
            let current = root;
            for (const seg of segments) {
                if (current == null) return undefined;
                current = current[seg];
            }
            return current;
        };

        // Build marker path segments e.g. ['User','Migrations', ...destinationPath.split('/')]
        const markerSegments = ['User', 'Migrations', ...this.destinationPath.split('/')];
        const existingMarker = getSettingFromSegments(settings, markerSegments);
        if (existingMarker !== undefined && existingMarker !== null) {
            // Migration already performed (marker exists) — exit early
            console.log(`Migration marker already present for ${this.migrationName} at ${migrationMarkerRelativePath}; skipping.`);
            return;
        }


        console.log(`No migration marker found locally for ${this.migrationName}; proceeding with migration.`);

        // Split paths into array segments
        const oldPathSegments = this.originalPath.split('/');
        const newPathSegments = this.destinationPath.split('/');
        
        // Get value from old path
        let originalSetting = settings;
        for (const segment of oldPathSegments) {
            if (originalSetting === undefined || originalSetting === null) {
                console.log(`Path ${this.originalPath} not found in settings`);
                appInsights?.trackException({
                    exception: new Error(`Path ${this.originalPath} not found in settings during migration ${this.migrationName}`),
                    severityLevel: 3, // Error
                    properties: {
                        migrationName: this.migrationName,
                        originalPath: this.originalPath,
                        destinationPath: this.destinationPath,
                        machineVersion: machine.version,
                        machineName: machine.machineName
                    }
                })
                return;
            }
            originalSetting = originalSetting[segment];
        }
        console.log(`Value at ${this.originalPath}:`, originalSetting);

        let destinationSetting = settings;
        for (const segment of newPathSegments) {
            if (destinationSetting === undefined || destinationSetting === null) {
                console.log(`Path ${this.destinationPath} not found in settings`);
                appInsights?.trackException({
                    exception: new Error(`Path ${this.destinationPath} not found in settings`),
                    severityLevel: 3, // Error
                    properties: {
                        migrationName: this.migrationName,
                        originalPath: this.originalPath,
                        destinationPath: this.destinationPath,
                        machineVersion: machine.version,
                        machineName: machine.machineName
                    }
                });
                return;
            }
            destinationSetting = destinationSetting[segment];
        }

        // Extract the value from the old setting
        let destinationNewValue = originalSetting.value;

        // Apply transform function if provided
        if (this.transformFunction) {
            const result = this.transformFunction(settings, originalSetting, destinationSetting);
            await originalSetting.Set(result.originalNewSetting.value);
            destinationNewValue = result.destinationNewSetting.value;
        }

        try {
            await destinationSetting.Set(destinationNewValue);

            // Mark migration as completed on the machine
            const postBody: any = {
                Requests: {
                    SetSetting: {
                        Path: `/Settings/${migrationMarkerRelativePath}`,
                        Value: "true",
                        Type: "boolean"
                    },
                },
            };
            await machine.connection.post(postBody);
            console.log(`Migration ${this.migrationName} from ${this.originalPath} to ${this.destinationPath} completed and marked at ${migrationMarkerRelativePath}`);
        } catch (error) {
            console.error(`Failed to migrate ${this.migrationName} from ${this.originalPath} to ${this.destinationPath}:`, error);
            appInsights?.trackException({
                exception: error instanceof Error ? error : new Error(String(error)),
                severityLevel: 3, // Error
                properties: {
                    migrationName: this.migrationName,
                    originalPath: this.originalPath,
                    destinationPath: this.destinationPath,
                    machineVersion: machine.version,
                    machineName: machine.machineName
                }
            });
        }
    };
}